import React from 'react';
import { Controller, UseFormReturn } from 'react-hook-form';
import { NumberFormatValues } from 'react-number-format';
import { InputNumberMask } from '../../../../../../../CommonComponents/OPL/Input/InputNumberMask';
import { AssumptionsDTO } from '../../../../api/useFirmSettingsQuery';
import { Text } from '../../../../../../../CommonComponents/OPL/Text/Text';
import { Select } from '../../../../../../../CommonComponents/OPL/Select/Select/Select';
import { AssumptionDefinition } from '../../Assumptions.meta';
import { OptionType } from '../../../../../../../CommonComponents/OPL/Select/BaseSelect/types';

const getSelectPlaceholderValue = (value: number | null, options: OptionType<number>[]) => {
  const found = options.find(({ value: _value }) => value === _value);

  return found?.label;
};

const getPlaceholder = (value?: string) => {
  return value ? `Use firm assumptions - ${value}` : undefined;
};

interface Props extends AssumptionDefinition {
  fieldName: string;
  isReadonly?: boolean;
  isFirm?: boolean;
  firmAssumptions: AssumptionsDTO;
  formBag: UseFormReturn<any>;
}

export const AssumptionField: React.FC<Props> = ({
  variant,
  fieldName,
  label,
  subFieldName,
  firmAssumptions,
  isFirm,
  formBag,
  isReadonly,
  options,
  canInheritFromFirm,
  toolTip,
}) => {
  const handleValueChange = ({ floatValue }: NumberFormatValues) => {
    formBag.setValue(
      `${fieldName}.${subFieldName}`,
      // When the input is empty, floatValue is undefined.
      // We need to convert undefined to null because we will reset the form with the
      // submitted values, and for react-hook-form these values cannot be undefined.
      floatValue === undefined ? null : floatValue,
      {
        shouldDirty: true,
        shouldValidate: true,
      },
    );
  };

  if (variant === 'percentage') {
    const placeholder = isFirm ? '' : getPlaceholder(`${firmAssumptions[subFieldName]}%`);

    return (
      <Controller
        control={formBag.control}
        name={`${fieldName}.${subFieldName}`}
        render={({ field: { value } }) => {
          return (
            <InputNumberMask
              value={value}
              label={label}
              onValueChange={handleValueChange}
              allowNegative={false}
              thousandSeparator={false}
              decimalScale={2}
              rightIcon="far fa-percent"
              disabled={isReadonly}
              placeholder={placeholder}
              tooltip={toolTip}
              helperText={formBag.formState.errors?.[fieldName]?.[subFieldName]?.message || ''}
              formVariant={
                formBag.formState.errors?.[fieldName]?.[subFieldName] ? 'error' : undefined
              }
            />
          );
        }}
      />
    );
  }

  if (variant === 'age' || variant === 'integer') {
    const placeholder = isFirm
      ? ''
      : getPlaceholder(`${firmAssumptions[subFieldName]} ${variant === 'age' ? 'years' : ''}`);

    return (
      <Controller
        control={formBag.control}
        name={`${fieldName}.${subFieldName}`}
        render={({ field: { value } }) => {
          return (
            <InputNumberMask
              value={value}
              label={label}
              onValueChange={handleValueChange}
              format={variant === 'age' ? '###' : undefined}
              allowNegative={false}
              thousandSeparator={false}
              decimalScale={0}
              disabled={isReadonly}
              placeholder={placeholder}
            />
          );
        }}
      />
    );
  }

  if (variant === 'select' && options) {
    const placeholder = isFirm
      ? ''
      : getPlaceholder(getSelectPlaceholderValue(firmAssumptions[subFieldName], options));

    const parsedOptions = [...options] as OptionType<number | null>[];

    if (canInheritFromFirm && !isFirm && placeholder) {
      parsedOptions.unshift({ label: placeholder, value: null });
    }

    return (
      <>
        <Text variant="formLabel" mb="4">
          {label}
        </Text>

        <Controller
          control={formBag.control}
          name={`${fieldName}.${subFieldName}`}
          render={({ field: { value } }) => (
            <Select
              value={parsedOptions.find(({ value: _value }) => value === _value)}
              options={parsedOptions}
              onChange={opt => {
                if (opt) {
                  formBag.setValue(`${fieldName}.${subFieldName}`, opt.value, {
                    shouldDirty: true,
                  });
                }
              }}
              disabled={isReadonly}
              placeholder={placeholder}
            />
          )}
        />
      </>
    );
  }

  return null;
};
